import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Save, Mail, Key, Eye, EyeOff } from 'lucide-react';

const AdminSettings = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailSettings, setEmailSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    from_email: ''
  });

  // Password change
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordMessage, setPasswordMessage] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchEmailSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          setEmailSettings(prev => ({ ...prev, ...data }));
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch email settings:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchEmailSettings();
  }, [fetchEmailSettings]);

  const handleSaveEmail = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const response = await fetch(`${API_URL}/api/admin/email-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailSettings)
      });

      if (response.ok) {
        setMessage('E-Mail Einstellungen gespeichert!');
      } else {
        setMessage('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Failed to save email settings:', error);
      setMessage('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage('');

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordMessage('Passwörter stimmen nicht überein');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });

      if (response.ok) {
        setPasswordMessage('Passwort erfolgreich geändert!');
        setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        const error = await response.json();
        setPasswordMessage(error.detail || 'Fehler beim Ändern des Passworts');
      }
    } catch (error) {
      console.error('Failed to change password:', error);
      setPasswordMessage('Fehler beim Ändern des Passworts');
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: '#1B4D3E' }}>Einstellungen</h1>
        <p className="text-gray-500">E-Mail und Kontoeinstellungen</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email Settings */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#1B4D3E' }}>
            <Mail className="w-5 h-5" />
            E-Mail Einstellungen (SMTP)
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Für Password-Reset E-Mails und Popup-Benachrichtigungen
          </p>

          <form onSubmit={handleSaveEmail} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">SMTP Host</label>
              <input
                type="text"
                value={emailSettings.smtp_host}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="smtp.example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">SMTP Port</label>
              <input
                type="number"
                value={emailSettings.smtp_port}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) }))}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">SMTP Benutzer</label>
              <input
                type="text"
                value={emailSettings.smtp_user}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_user: e.target.value }))}
                className="w-full px-4 py-2 border rounded-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">SMTP Passwort</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={emailSettings.smtp_password}
                  onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg pr-12"
                  placeholder="Neues Passwort eingeben"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  {showPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Absender E-Mail</label>
              <input
                type="email"
                value={emailSettings.from_email}
                onChange={(e) => setEmailSettings(prev => ({ ...prev, from_email: e.target.value }))}
                className="w-full px-4 py-2 border rounded-lg"
                placeholder="noreply@example.com"
              />
            </div>

            {message && (
              <div className={`p-3 rounded-lg text-sm ${message.includes('erfolgreich') || message.includes('gespeichert') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {message}
              </div>
            )}

            <Button
              type="submit"
              disabled={saving}
              className="w-full"
              style={{ backgroundColor: '#1B4D3E' }}
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Speichern...' : 'Speichern'}
            </Button>
          </form>
        </div>

        {/* Password Change */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#1B4D3E' }}>
            <Key className="w-5 h-5" />
            Eigenes Passwort ändern
          </h2>

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Aktuelles Passwort</label>
              <input
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                className="w-full px-4 py-2 border rounded-lg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Neues Passwort</label>
              <input
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                className="w-full px-4 py-2 border rounded-lg"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Neues Passwort bestätigen</label>
              <input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                className="w-full px-4 py-2 border rounded-lg"
                required
              />
            </div>

            {passwordMessage && (
              <div className={`p-3 rounded-lg text-sm ${passwordMessage.includes('erfolgreich') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {passwordMessage}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              style={{ backgroundColor: '#1B4D3E' }}
            >
              Passwort ändern
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
